import type { QuantityValue } from '@/types/crm';

export interface CurrencyValueLabelProps {
  value: QuantityValue | null | undefined;
  fontSize?: 'xs' | 'table' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl';
  className?: string;
  /** Enable CSS truncation with ellipsis and title tooltip */
  truncate?: boolean;
}

const fontSizeClasses = {
  xs: 'text-xs',
  table: 'text-[13px]',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl',
};

/**
 * Display component for currency values.
 * Always formats values using Intl.NumberFormat with currency style.
 *
 * @example
 * <CurrencyValueLabel value={{amount: 1000, unit: 'USD'}} />
 * // Renders: $1,000.00
 */
export const CurrencyValueLabel: React.FC<CurrencyValueLabelProps> = ({
  value,
  fontSize = 'sm',
  className = '',
  truncate,
}) => {
  const sizeClass = fontSizeClasses[fontSize];
  const truncateClasses = truncate
    ? 'block overflow-hidden text-ellipsis whitespace-nowrap'
    : '';

  // Convert string amounts to numbers for numeric operations
  const numAmount =
    typeof value?.amount === 'string'
      ? parseFloat(value.amount)
      : value?.amount;

  if (
    !value ||
    value.amount === null ||
    value.amount === undefined ||
    numAmount === undefined ||
    isNaN(numAmount)
  ) {
    return <span className={`${sizeClass} ${className}`}>—</span>;
  }

  if (value.unit) {
    try {
      // Let Intl.NumberFormat determine the correct fraction digits per currency
      // (e.g., JPY uses 0 decimals, USD uses 2, KWD uses 3)
      const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: value.unit,
      }).format(numAmount);
      return (
        <span
          className={`${sizeClass} ${truncateClasses} ${className}`}
          title={truncate ? formatted : undefined}
        >
          {formatted}
        </span>
      );
    } catch {
      // Fallback if currency code is invalid
      const fallback = `${numAmount.toLocaleString()} ${value.unit}`;
      return (
        <span
          className={`${sizeClass} ${truncateClasses} ${className}`}
          title={truncate ? fallback : undefined}
        >
          {fallback}
        </span>
      );
    }
  }

  // Fallback for values without unit
  const fallback = numAmount.toLocaleString();
  return (
    <span
      className={`${sizeClass} ${truncateClasses} ${className}`}
      title={truncate ? fallback : undefined}
    >
      {fallback}
    </span>
  );
};
